import { useEffect, useState, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { IoIosArrowBack } from "react-icons/io";

// UI IMPORT
import {
    Button,
    ControlledDatePicker,
    Table,
    TableRow,
    getStatusTypes,
} from "../../ui";

// PROJECT IMPORT
import {
    dateFormatter,
    prepareMaxDate,
    seperator,
} from "../../helperFunctions";
import { useGetMerchantWithdrawalMutation } from "./withdrawalApi";
import { summaryData } from "./mock";
import { Card } from "../common";

const MerchantWithdrawal = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const stateDate = location.state?.date ? new Date(location.state.date) : new Date();
    const merchantId = location.state?.merchantId || "";
    const merchantName = location.state?.merchantName || "";

    const [filter, setFilter] = useState({
        date: stateDate,
        merchantId,
    });

    const [pagination, setPagination] = useState({
        limit: 50,
        page_no: 1,
    });

    const [getMerchantWithdrawal, { data, isLoading }] = useGetMerchantWithdrawalMutation();

    const { handleSubmit, control, watch, reset } = useForm<any>({
        defaultValues: {
            date: stateDate,
        },
    });

    useEffect(() => {
        const date = location.state?.date ? new Date(location.state.date) : new Date();
        const mId = location.state?.merchantId || "";

        setFilter((prev) => {
            if (prev.date.getTime() === date.getTime() && prev.merchantId === mId) {
                return prev;
            }
            return {
                date: date,
                merchantId: mId,
            };
        });

        reset({
            date: date,
        });
    }, [location.state?.date, location.state?.merchantId, reset]);

    const minDate = data?.filterStartDate ? new Date(data.filterStartDate) : undefined;
    const maxDate = data?.filterEndDate ? new Date(data.filterEndDate) : new Date();

    const columns = [
        {
            title: "Txn ID",
            name: "txn_id",
        },
        {
            title: "Amount",
            name: "amount",
            Cell: (item: any) => <span>{seperator(item?.amount)}</span>,
        },
        {
            title: "Fees",
            name: "pg_fees",
            Cell: (item: any) => <span>{seperator(item?.pg_fees)}</span>,
        },
        {
            title: "Commission Amount",
            name: "commission_amount",
            Cell: (item: any) => <span>{seperator(item?.commission_amount)}</span>,
        },
        {
            title: "Status",
            name: "status",
            Cell: (item: any) => (
                <span className={getStatusTypes(item?.status)}>
                    {item?.status}
                </span>
            ),
        },
        {
            title: "Created At",
            name: "created_at",
            Cell: (item: any) => (
                <span>{item?.created_at}</span>
            ),
        },
    ];

    const onGetData = async () => {
        const payload: any = {
            page_no: pagination.page_no,
            limit: pagination.limit,
            filter_data: {
                startDate: dateFormatter(filter.date, "start"),
                endDate: dateFormatter(filter.date, "end"),
                merchantId: filter.merchantId,
            },
        };
        getMerchantWithdrawal(payload);
    };

    useEffect(() => {
        if (filter.merchantId) {
            onGetData();
        }
    }, [filter, pagination]);



    const onSubmit = async (values: any) => {
        setFilter((prev) => ({
            ...prev,
            date: values.date || new Date(),
        }));
        setPagination({ limit: 50, page_no: 1 });
    };

    // Flatten the response data to get transactions
    const transactions: any[] = [];
    let totalPayoutTxn = 0;

    if (data?.data) {
        Object.entries(data.data).forEach(([dateKey, dateItem]: any) => {
            if (dateItem?.data) {
                Object.entries(dateItem.data).forEach(([mId, merchantItem]: any) => {
                    if (merchantItem) {
                        Object.entries(merchantItem).forEach(([commId, commItem]: any) => {
                            if (commItem?.transactions) {
                                const enrichedTransactions = commItem.transactions.map((t: any) => ({
                                    ...t,
                                    commission_per: commItem.commission_per,
                                }));
                                transactions.push(...enrichedTransactions);
                            }
                        });
                    }
                });
            }
        });
        totalPayoutTxn = data?.summary?.total_payout_txn || transactions.length;
    }

    return (
        <>
            <div className="mt-2">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex gap-4 items-center">
                        <Button
                            onClick={() => navigate(-1)}
                            variant="Transaparent"
                            className="!h-9 !text-sm border border-gray-300 flex items-center !px-2 gap-2"
                        >
                            <IoIosArrowBack />
                            Back
                        </Button>
                        <h2 className="text-lg font-semibold">
                            Merchant Withdrawal Details {merchantName ? `- ${merchantName}` : ""}
                        </h2>
                    </div>


                    <form
                        className="flex flex-wrap gap-2 items-start justify-end px-1"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <ControlledDatePicker
                            name="date"
                            label="Date"
                            placeholder="Date"
                            minDate={minDate}
                            maxDate={maxDate}
                            control={control}
                        />
                        <Button type="submit" className="mt-[22px] !h-9 !text-sm">
                            Apply
                        </Button>
                    </form>
                </div>

                <div className="mt-2">
                    <div className="grid grid-cols-5 sm:grid-cols-2 md:grid-cols-2 gap-4 mainbox">
                        {summaryData?.map((item: any, index) => {
                            return (
                                <Fragment key={index}>
                                    <Card
                                        length={summaryData.length}
                                        index={index}
                                        value={data?.summary?.[item.key]}
                                        text={item?.text}
                                        icon={item?.imgSrc}
                                        rupee={item?.rupee}
                                    />
                                </Fragment>
                            );
                        })}
                    </div>
                    <div className="App mt-3 tableborder">
                        <Table
                            isExpendable={false}
                            columns={columns}
                            isLoading={isLoading}
                            data={transactions}
                            pagination={pagination}
                            handlePaginationChange={(params) =>
                                setPagination((prev) => ({ ...prev, ...params }))
                            }
                            count={totalPayoutTxn}
                            minHeight={400}
                        >
                            {transactions.map((item: any, index: number) => {
                                return (
                                    <Fragment key={index}>
                                        <TableRow columns={columns} item={item} isExpendable={false}></TableRow>
                                    </Fragment>
                                );
                            })}
                        </Table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MerchantWithdrawal;
